// src/components/auth/EmailAuthDialog.tsx
import { useMemo, useState } from "react";

// src/lib/auth-client.ts
import { createAuthClient } from "better-auth/client";
import { jazzPluginClient } from "jazz-tools/better-auth/auth/client";
function createBetterAuthClient(authUrl) {
  const baseURL = authUrl ? `${authUrl}/api/auth` : `${window.location.origin}/api/auth`;
  return createAuthClient({
    baseURL,
    plugins: [jazzPluginClient()],
    fetchOptions: {
      credentials: "include"
    }
  });
}
var _client;
function getBetterAuthClient() {
  if (!_client) {
    _client = createBetterAuthClient();
  }
  return _client;
}

// src/components/ui/Button.tsx
import { cva } from "class-variance-authority";
import { forwardRef } from "react";

// src/lib/utils.ts
import {
  getNodeId
} from "@jbr-jazz/hierarchy-shared";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  try {
    return twMerge(clsx(inputs));
  } catch {
    return clsx(inputs);
  }
}
function buildActionNodeData(node, folder) {
  const isFolder = node.type === "folder";
  return {
    id: getNodeId(node),
    name: node.name,
    type: node.type,
    sharingMode: node.sharingMode,
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
    expanded: isFolder ? folder?.expanded ?? false : void 0
  };
}

// src/components/ui/Button.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = cva(
  // Base styles
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--jh-focus-ring,rgba(59,130,246,0.2))] disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-[var(--jh-primary,#3b82f6)] text-white hover:bg-[var(--jh-primary-hover,#2563eb)]",
        secondary: "border border-[var(--jh-border-primary,#e5e7eb)] bg-[var(--jh-bg-elevated,#ffffff)] text-[var(--jh-text-primary,#171717)] hover:bg-[var(--jh-bg-hover,#f5f5f5)]",
        outline: "border border-[var(--jh-primary,#3b82f6)] bg-transparent text-[var(--jh-primary,#3b82f6)] hover:bg-[var(--jh-primary-light,rgba(59,130,246,0.1))]",
        ghost: "hover:bg-[var(--jh-bg-hover,#f5f5f5)] text-[var(--jh-text-primary,#171717)]",
        danger: "bg-[var(--jh-danger,#ef4444)] text-white hover:bg-[var(--jh-danger-hover,#dc2626)]",
        link: "text-[var(--jh-primary,#3b82f6)] underline-offset-4 hover:underline"
      },
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-10 px-4",
        lg: "h-11 px-6",
        icon: "h-10 w-10 p-0"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);
var Button = forwardRef(
  ({ className, variant, size, isLoading, children, disabled, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        type: "button",
        className: cn(buttonVariants({ variant, size, className })),
        disabled: disabled || isLoading,
        ...props,
        children: [
          isLoading && /* @__PURE__ */ jsx("div", { className: "h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" }),
          children
        ]
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ui/Dialog.tsx
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx2(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn("fixed inset-0 z-50 bg-black/50", className),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React.forwardRef(({ className = "", children, ...props }, ref) => /* @__PURE__ */ jsxs2(DialogPortal, { children: [
  /* @__PURE__ */ jsx2(DialogOverlay, {}),
  /* @__PURE__ */ jsx2(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        "fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] rounded-lg border border-[var(--jh-border-primary,#e5e7eb)] bg-[var(--jh-bg-elevated,#ffffff)] p-6 shadow-lg",
        className
      ),
      ...props,
      children
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({ className = "", ...props }) => /* @__PURE__ */ jsx2("div", { className: cn("flex flex-col space-y-3 text-center sm:text-left", className), ...props });
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({ className = "", ...props }) => /* @__PURE__ */ jsx2(
  "div",
  {
    className: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx2(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight text-[var(--jh-text-primary,#171717)]",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx2(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-[var(--jh-text-secondary,#525252)] leading-relaxed", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/components/ui/Icons.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var createIcon = (path, viewBox = "0 0 24 24") => {
  const Icon = ({ className, ...props }) => /* @__PURE__ */ jsx3(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox,
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      ...props,
      children: /* @__PURE__ */ jsx3("path", { d: path })
    }
  );
  Icon.displayName = "Icon";
  return Icon;
};
var ArrowLeftIcon = createIcon("M19 12H5M12 19l-7-7 7-7");
var Loader2Icon = ({ className, ...props }) => /* @__PURE__ */ jsx3(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    ...props,
    children: /* @__PURE__ */ jsx3("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" })
  }
);
Loader2Icon.displayName = "Loader2Icon";
var MailIcon = ({ className, ...props }) => /* @__PURE__ */ jsxs3(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    ...props,
    children: [
      /* @__PURE__ */ jsx3("rect", { width: "20", height: "16", x: "2", y: "4", rx: "2" }),
      /* @__PURE__ */ jsx3("path", { d: "m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" })
    ]
  }
);
MailIcon.displayName = "MailIcon";
var PlusIcon = createIcon("M5 12h14M12 5v14");
var Trash2Icon = ({ className, ...props }) => /* @__PURE__ */ jsxs3(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    ...props,
    children: [
      /* @__PURE__ */ jsx3("path", { d: "M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" }),
      /* @__PURE__ */ jsx3("line", { x1: "10", x2: "10", y1: "11", y2: "17" }),
      /* @__PURE__ */ jsx3("line", { x1: "14", x2: "14", y1: "11", y2: "17" })
    ]
  }
);
Trash2Icon.displayName = "Trash2Icon";
var XIcon = createIcon("M18 6 6 18M6 6l12 12");

// src/components/ui/Input.tsx
import { cva as cva2 } from "class-variance-authority";
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var inputVariants = cva2(
  "flex w-full rounded-md bg-[var(--jh-bg-elevated,#ffffff)] text-[var(--jh-text-primary,#171717)] text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[var(--jh-text-tertiary,#a3a3a3)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "h-10 border border-[var(--jh-border-primary,#e5e7eb)] px-3 py-2 focus:border-[var(--jh-primary,#3b82f6)] focus:ring-2 focus:ring-[var(--jh-focus-ring,rgba(59,130,246,0.2))]",
        inline: "border border-[var(--jh-primary,#3b82f6)] px-2 py-0.5 focus:ring-2 focus:ring-[var(--jh-focus-ring,rgba(59,130,246,0.2))]"
      },
      state: {
        default: "",
        error: "border-[var(--jh-danger,#ef4444)] focus:border-[var(--jh-danger,#ef4444)] focus:ring-[var(--jh-danger-light,rgba(239,68,68,0.2))]"
      }
    },
    defaultVariants: {
      variant: "default",
      state: "default"
    }
  }
);
var Input = forwardRef3(
  ({ className, variant, state, type = "text", ...props }, ref) => {
    return /* @__PURE__ */ jsx4(
      "input",
      {
        ref,
        type,
        className: cn(inputVariants({ variant, state, className })),
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/components/ui/Label.tsx
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var Label = forwardRef4(
  ({ className, required, children, ...props }, ref) => {
    return /* @__PURE__ */ jsxs4(
      "label",
      {
        ref,
        className: cn("text-sm font-medium text-[var(--jh-text-primary,#171717)]", className),
        ...props,
        children: [
          children,
          required && /* @__PURE__ */ jsx5("span", { className: "text-[var(--jh-danger,#ef4444)] ml-1", children: "*" })
        ]
      }
    );
  }
);
Label.displayName = "Label";

// src/components/auth/EmailAuthDialog.tsx
import { Fragment, jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function EmailAuthDialog({ open, onOpenChange, ui = {}, onSuccess }) {
  const Button2 = ui.Button ?? Button;
  const Dialog2 = ui.Dialog ?? Dialog;
  const DialogContent2 = ui.DialogContent ?? DialogContent;
  const DialogHeader2 = ui.DialogHeader ?? DialogHeader;
  const DialogTitle2 = ui.DialogTitle ?? DialogTitle;
  const DialogDescription2 = ui.DialogDescription ?? DialogDescription;
  const Input2 = ui.Input ?? Input;
  const Label3 = ui.Label ?? Label;
  const ArrowLeftIcon2 = ui.ArrowLeftIcon ?? ArrowLeftIcon;
  const betterAuthClient = useMemo(() => getBetterAuthClient(), []);
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setError(null);
    setMessage(null);
    setShowResendVerification(false);
  };
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setError(null);
    setMessage(null);
    setShowResendVerification(false);
  };
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      resetForm();
      setMode("signin");
    }
    onOpenChange(isOpen);
  };
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const result = await betterAuthClient.signIn.email({
        email,
        password
      });
      if (result.error) {
        if (result.error.message?.toLowerCase().includes("verif")) {
          setError("Please verify your email first.");
          setShowResendVerification(true);
        } else {
          setError(result.error.message || "Invalid email or password");
          setShowResendVerification(false);
        }
      } else {
        onOpenChange(false);
        resetForm();
        if (onSuccess) {
          onSuccess();
        } else {
          window.location.reload();
        }
      }
    } catch {
      setError("Sign in failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const result = await betterAuthClient.signUp.email({
        email,
        password,
        name: name || email.split("@")[0],
        callbackURL: `${window.location.origin}?verified=true`
      });
      if (result.error) {
        setError(result.error.message || "Sign up failed");
      } else {
        setMode("check-email");
        setMessage("Check your email to verify your account.");
      }
    } catch {
      setError("Sign up failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const client = betterAuthClient;
      if (typeof client.forgetPassword === "function") {
        await client.forgetPassword({
          email,
          redirectTo: `${window.location.origin}/reset-password`
        });
      } else if (typeof client.resetPassword === "function") {
        await client.resetPassword({
          email,
          redirectTo: `${window.location.origin}/reset-password`
        });
      }
      setMode("check-email");
      setMessage("Check your email for a password reset link.");
    } catch {
      setError("Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await betterAuthClient.sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}?verified=true`
      });
      setMode("check-email");
      setMessage("Verification email sent. Check your inbox.");
    } catch {
      setError("Failed to send verification email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const renderCheckEmail = () => /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5(DialogHeader2, { children: [
      /* @__PURE__ */ jsx6(DialogTitle2, { children: "Check Your Email" }),
      /* @__PURE__ */ jsx6(DialogDescription2, { children: message })
    ] }),
    /* @__PURE__ */ jsx6("div", { className: "py-4", children: /* @__PURE__ */ jsx6(
      Button2,
      {
        type: "button",
        variant: "outline",
        className: "w-full",
        onClick: () => handleOpenChange(false),
        children: "Done"
      }
    ) })
  ] });
  const renderForgotPassword = () => /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5(DialogHeader2, { children: [
      /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-2", children: [
        ArrowLeftIcon2 && /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            onClick: () => handleModeChange("signin"),
            className: "rounded-full p-1 hover:bg-muted",
            "aria-label": "Back to sign in",
            children: /* @__PURE__ */ jsx6(ArrowLeftIcon2, { className: "h-4 w-4" })
          }
        ),
        /* @__PURE__ */ jsx6(DialogTitle2, { children: "Reset Password" })
      ] }),
      /* @__PURE__ */ jsx6(DialogDescription2, { children: "Enter your email and we'll send you a reset link" })
    ] }),
    /* @__PURE__ */ jsxs5("form", { onSubmit: handleForgotPassword, className: "space-y-4 py-4", children: [
      /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "forgot-email", children: "Email" }),
        /* @__PURE__ */ jsx6(
          Input2,
          {
            id: "forgot-email",
            type: "email",
            value: email,
            onChange: (e) => setEmail(e.target.value),
            placeholder: "you@example.com",
            required: true,
            autoComplete: "email"
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx6("p", { className: "text-sm text-red-500", children: error }),
      /* @__PURE__ */ jsx6(Button2, { type: "submit", className: "w-full", disabled: isLoading, children: isLoading ? "Sending..." : "Send Reset Link" })
    ] })
  ] });
  const renderResendVerification = () => /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5(DialogHeader2, { children: [
      /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-2", children: [
        ArrowLeftIcon2 && /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            onClick: () => handleModeChange("signin"),
            className: "rounded-full p-1 hover:bg-muted",
            "aria-label": "Back to sign in",
            children: /* @__PURE__ */ jsx6(ArrowLeftIcon2, { className: "h-4 w-4" })
          }
        ),
        /* @__PURE__ */ jsx6(DialogTitle2, { children: "Resend Verification" })
      ] }),
      /* @__PURE__ */ jsx6(DialogDescription2, { children: "Enter your email to receive a new verification link" })
    ] }),
    /* @__PURE__ */ jsxs5("form", { onSubmit: handleResendVerification, className: "space-y-4 py-4", children: [
      /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "resend-email", children: "Email" }),
        /* @__PURE__ */ jsx6(
          Input2,
          {
            id: "resend-email",
            type: "email",
            value: email,
            onChange: (e) => setEmail(e.target.value),
            placeholder: "you@example.com",
            required: true,
            autoComplete: "email"
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx6("p", { className: "text-sm text-red-500", children: error }),
      /* @__PURE__ */ jsx6(Button2, { type: "submit", className: "w-full", disabled: isLoading, children: isLoading ? "Sending..." : "Resend Verification Email" })
    ] })
  ] });
  const renderSignIn = () => /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5(DialogHeader2, { children: [
      /* @__PURE__ */ jsx6(DialogTitle2, { children: "Sign In with Email" }),
      /* @__PURE__ */ jsx6(DialogDescription2, { children: "Enter your email and password" })
    ] }),
    /* @__PURE__ */ jsxs5("form", { onSubmit: handleEmailSignIn, className: "space-y-4 py-4", children: [
      /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "signin-email", children: "Email" }),
        /* @__PURE__ */ jsx6(
          Input2,
          {
            id: "signin-email",
            type: "email",
            value: email,
            onChange: (e) => setEmail(e.target.value),
            placeholder: "you@example.com",
            required: true,
            autoComplete: "email"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "signin-password", children: "Password" }),
        /* @__PURE__ */ jsx6(
          Input2,
          {
            id: "signin-password",
            type: "password",
            value: password,
            onChange: (e) => setPassword(e.target.value),
            placeholder: "Your password",
            required: true,
            autoComplete: "current-password"
          }
        )
      ] }),
      error && /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6("p", { className: "text-sm text-red-500", children: error }),
        showResendVerification && /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            className: "text-sm text-blue-600 hover:text-blue-800 hover:underline",
            onClick: () => handleModeChange("resend-verification"),
            children: "Resend verification email"
          }
        )
      ] }),
      /* @__PURE__ */ jsx6(Button2, { type: "submit", className: "w-full", disabled: isLoading, children: isLoading ? "Signing in..." : "Sign In" }),
      /* @__PURE__ */ jsxs5("div", { className: "flex justify-between text-sm", children: [
        /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            className: "text-muted-foreground hover:text-foreground",
            onClick: () => handleModeChange("forgot-password"),
            children: "Forgot password?"
          }
        ),
        /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            className: "text-muted-foreground hover:text-foreground",
            onClick: () => handleModeChange("signup"),
            children: "Create account"
          }
        )
      ] })
    ] })
  ] });
  const renderSignUp = () => /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5(DialogHeader2, { children: [
      /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-2", children: [
        ArrowLeftIcon2 && /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            onClick: () => handleModeChange("signin"),
            className: "rounded-full p-1 hover:bg-muted",
            "aria-label": "Back to sign in",
            children: /* @__PURE__ */ jsx6(ArrowLeftIcon2, { className: "h-4 w-4" })
          }
        ),
        /* @__PURE__ */ jsx6(DialogTitle2, { children: "Create Account" })
      ] }),
      /* @__PURE__ */ jsx6(DialogDescription2, { children: "Create an account with your email" })
    ] }),
    /* @__PURE__ */ jsxs5("form", { onSubmit: handleEmailSignUp, className: "space-y-4 py-4", children: [
      /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "signup-name", children: "Display Name (optional)" }),
        /* @__PURE__ */ jsx6(
          Input2,
          {
            id: "signup-name",
            type: "text",
            value: name,
            onChange: (e) => setName(e.target.value),
            placeholder: "Your name",
            autoComplete: "name"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "signup-email", children: "Email" }),
        /* @__PURE__ */ jsx6(
          Input2,
          {
            id: "signup-email",
            type: "email",
            value: email,
            onChange: (e) => setEmail(e.target.value),
            placeholder: "you@example.com",
            required: true,
            autoComplete: "email"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "signup-password", children: "Password" }),
        /* @__PURE__ */ jsx6(
          Input2,
          {
            id: "signup-password",
            type: "password",
            value: password,
            onChange: (e) => setPassword(e.target.value),
            placeholder: "At least 8 characters",
            required: true,
            minLength: 8,
            autoComplete: "new-password"
          }
        )
      ] }),
      error && /* @__PURE__ */ jsx6("p", { className: "text-sm text-red-500", children: error }),
      /* @__PURE__ */ jsx6(Button2, { type: "submit", className: "w-full", disabled: isLoading, children: isLoading ? "Creating account..." : "Create Account" })
    ] })
  ] });
  return /* @__PURE__ */ jsx6(Dialog2, { open, onOpenChange: handleOpenChange, children: /* @__PURE__ */ jsxs5(DialogContent2, { className: "sm:max-w-md", children: [
    mode === "check-email" && renderCheckEmail(),
    mode === "forgot-password" && renderForgotPassword(),
    mode === "resend-verification" && renderResendVerification(),
    mode === "signin" && renderSignIn(),
    mode === "signup" && renderSignUp()
  ] }) });
}

// src/components/auth/LinkedEmailsSection.tsx
import { useCallback, useEffect, useMemo as useMemo2, useState as useState2 } from "react";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function LinkedEmailsSection({ ui = {} }) {
  const Button2 = ui.Button ?? Button;
  const Loader2Icon2 = ui.Loader2Icon ?? Loader2Icon;
  const MailIcon2 = ui.MailIcon ?? MailIcon;
  const PlusIcon2 = ui.PlusIcon ?? PlusIcon;
  const Trash2Icon2 = ui.Trash2Icon ?? Trash2Icon;
  const XIcon2 = ui.XIcon ?? XIcon;
  const betterAuthClient = useMemo2(() => getBetterAuthClient(), []);
  const [primaryEmail, setPrimaryEmail] = useState2(null);
  const [verifiedEmails, setVerifiedEmails] = useState2([]);
  const [isLoading, setIsLoading] = useState2(true);
  const [isAdding, setIsAdding] = useState2(false);
  const [newEmail, setNewEmail] = useState2("");
  const [error, setError] = useState2(null);
  const [successMessage, setSuccessMessage] = useState2(null);
  const [deletingId, setDeletingId] = useState2(null);
  const fetchData = useCallback(async () => {
    try {
      const session = await betterAuthClient.getSession();
      if (session?.data?.user?.email) {
        setPrimaryEmail(session.data.user.email);
      }
      const verifiedResponse = await fetch("/api/verified-emails", {
        credentials: "include"
      });
      if (verifiedResponse.ok) {
        const data = await verifiedResponse.json();
        setVerifiedEmails(data.emails || []);
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  }, [betterAuthClient]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;
    setError(null);
    setSuccessMessage(null);
    setIsAdding(true);
    try {
      const response = await fetch("/api/verified-emails/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: newEmail.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(`Verification email sent to ${newEmail}`);
        setNewEmail("");
      } else {
        setError(data.error || "Failed to send verification email");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setIsAdding(false);
    }
  };
  const handleRemoveEmail = async (id) => {
    setDeletingId(id);
    setError(null);
    try {
      const response = await fetch(`/api/verified-emails/${id}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        setVerifiedEmails((prev) => prev.filter((e) => e.id !== id));
      } else {
        const data = await response.json();
        setError(data.error || "Failed to remove email");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };
  if (isLoading) {
    return /* @__PURE__ */ jsx7("div", { className: "flex items-center justify-center py-4", children: /* @__PURE__ */ jsx7(Loader2Icon2, { className: "h-5 w-5 animate-spin text-content-secondary" }) });
  }
  return /* @__PURE__ */ jsxs6("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx7("div", { className: "text-sm font-medium text-content-primary", children: "Linked Emails" }),
    /* @__PURE__ */ jsx7("p", { className: "text-xs text-content-secondary", children: "Add additional email addresses to receive shares sent to those addresses." }),
    error && /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400", children: [
      /* @__PURE__ */ jsx7(XIcon2, { className: "h-4 w-4 flex-shrink-0" }),
      error
    ] }),
    successMessage && /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400", children: [
      /* @__PURE__ */ jsx7(MailIcon2, { className: "h-4 w-4 flex-shrink-0" }),
      successMessage
    ] }),
    /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 rounded-md border border-border-default bg-surface-secondary px-3 py-2", children: [
      /* @__PURE__ */ jsx7(MailIcon2, { className: "h-4 w-4 text-content-secondary" }),
      /* @__PURE__ */ jsx7("span", { className: "flex-1 text-sm", children: primaryEmail }),
      /* @__PURE__ */ jsx7("span", { className: "text-xs text-content-tertiary", children: "Primary" })
    ] }),
    verifiedEmails.map((email) => /* @__PURE__ */ jsxs6(
      "div",
      {
        className: "flex items-center gap-2 rounded-md border border-border-default bg-surface-primary px-3 py-2",
        children: [
          /* @__PURE__ */ jsx7(MailIcon2, { className: "h-4 w-4 text-content-secondary" }),
          /* @__PURE__ */ jsx7("span", { className: "flex-1 text-sm", children: email.email }),
          /* @__PURE__ */ jsx7(
            "button",
            {
              type: "button",
              onClick: () => handleRemoveEmail(email.id),
              disabled: deletingId === email.id,
              className: "rounded p-1 text-content-tertiary hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-900/20",
              title: "Remove email",
              children: deletingId === email.id ? /* @__PURE__ */ jsx7(Loader2Icon2, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx7(Trash2Icon2, { className: "h-4 w-4" })
            }
          )
        ]
      },
      email.id
    )),
    /* @__PURE__ */ jsxs6("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx7(
        "input",
        {
          type: "email",
          value: newEmail,
          onChange: (e) => setNewEmail(e.target.value),
          placeholder: "Add email address",
          className: "flex-1 rounded-md border border-border-default bg-surface-primary px-3 py-2 text-sm placeholder:text-content-tertiary focus:border-accent-primary focus:outline-none focus:ring-1 focus:ring-accent-primary",
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddEmail();
            }
          }
        }
      ),
      /* @__PURE__ */ jsxs6(
        Button2,
        {
          type: "button",
          variant: "outline",
          size: "sm",
          onClick: handleAddEmail,
          disabled: isAdding || !newEmail.trim(),
          className: "gap-1",
          children: [
            isAdding ? /* @__PURE__ */ jsx7(Loader2Icon2, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx7(PlusIcon2, { className: "h-4 w-4" }),
            "Add"
          ]
        }
      )
    ] })
  ] });
}

// src/components/auth/ProfileDialog.tsx
import { useState as useState3 } from "react";

// src/components/ui/AlertDialog.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function AlertDialog({ open, onOpenChange, children }) {
  return /* @__PURE__ */ jsx8(Dialog, { open, onOpenChange, children });
}
function AlertDialogContent({ children }) {
  return /* @__PURE__ */ jsx8(DialogContent, { className: "sm:max-w-md", children });
}
function AlertDialogHeader({ children }) {
  return /* @__PURE__ */ jsx8(DialogHeader, { children });
}
function AlertDialogTitle({ children }) {
  return /* @__PURE__ */ jsx8(DialogTitle, { children });
}
function AlertDialogDescription({ children }) {
  return /* @__PURE__ */ jsx8(DialogDescription, { children });
}
function AlertDialogFooter({ children }) {
  return /* @__PURE__ */ jsx8(DialogFooter, { children });
}
function AlertDialogAction({
  children,
  onClick
}) {
  return /* @__PURE__ */ jsx8(Button, { variant: "danger", onClick, children });
}
function AlertDialogCancel({ children }) {
  return /* @__PURE__ */ jsx8(Button, { variant: "secondary", children });
}

// src/components/auth/ProfileDialog.tsx
import { Fragment as Fragment2, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
function ProfileDialog({
  open,
  onOpenChange,
  ui = {},
  onSignOut,
  onDeleteAccount,
  theme,
  userName,
  userEmail
}) {
  const Dialog2 = ui.Dialog ?? Dialog;
  const DialogContent2 = ui.DialogContent ?? DialogContent;
  const DialogHeader2 = ui.DialogHeader ?? DialogHeader;
  const DialogTitle2 = ui.DialogTitle ?? DialogTitle;
  const AlertDialog2 = ui.AlertDialog ?? AlertDialog;
  const AlertDialogContent2 = ui.AlertDialogContent ?? AlertDialogContent;
  const AlertDialogHeader2 = ui.AlertDialogHeader ?? AlertDialogHeader;
  const AlertDialogTitle2 = ui.AlertDialogTitle ?? AlertDialogTitle;
  const AlertDialogDescription2 = ui.AlertDialogDescription ?? AlertDialogDescription;
  const AlertDialogFooter2 = ui.AlertDialogFooter ?? AlertDialogFooter;
  const AlertDialogAction2 = ui.AlertDialogAction ?? AlertDialogAction;
  const AlertDialogCancel2 = ui.AlertDialogCancel ?? AlertDialogCancel;
  const Button2 = ui.Button ?? Button;
  const { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } = ui;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState3(false);
  const handleSignOut = () => {
    onSignOut();
    onOpenChange(false);
  };
  const handleDeleteAccount = () => {
    setShowDeleteConfirm(false);
    onDeleteAccount();
    onOpenChange(false);
  };
  return /* @__PURE__ */ jsxs7(Fragment2, { children: [
    /* @__PURE__ */ jsx9(Dialog2, { open, onOpenChange, children: /* @__PURE__ */ jsxs7(DialogContent2, { className: "sm:max-w-md", children: [
      /* @__PURE__ */ jsx9(DialogHeader2, { children: /* @__PURE__ */ jsx9(DialogTitle2, { children: "Profile Settings" }) }),
      /* @__PURE__ */ jsxs7("div", { className: "space-y-6 py-4", children: [
        (userName || userEmail) && /* @__PURE__ */ jsxs7("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx9("div", { className: "text-sm font-medium text-content-primary", children: "Account" }),
          userName && /* @__PURE__ */ jsx9("p", { className: "text-sm text-content-secondary", children: userName }),
          userEmail && /* @__PURE__ */ jsx9("p", { className: "text-sm text-content-tertiary", children: userEmail })
        ] }),
        theme && Select && SelectTrigger && SelectValue && SelectContent && SelectItem && /* @__PURE__ */ jsxs7("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx9("div", { className: "text-sm font-medium text-content-primary", children: "Theme" }),
          /* @__PURE__ */ jsxs7(
            Select,
            {
              value: theme.theme,
              onValueChange: (value) => {
                if (value === "light" || value === "dark" || value === "system") {
                  theme.setTheme(value);
                }
              },
              children: [
                /* @__PURE__ */ jsx9(SelectTrigger, { children: /* @__PURE__ */ jsx9(SelectValue, { placeholder: "Select theme" }) }),
                /* @__PURE__ */ jsxs7(SelectContent, { children: [
                  /* @__PURE__ */ jsx9(SelectItem, { value: "light", children: "Light" }),
                  /* @__PURE__ */ jsx9(SelectItem, { value: "dark", children: "Dark" }),
                  /* @__PURE__ */ jsx9(SelectItem, { value: "system", children: "System" })
                ] })
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx9(LinkedEmailsSection, { ui }),
        /* @__PURE__ */ jsxs7("div", { className: "space-y-2 pt-4 border-t border-border-default", children: [
          /* @__PURE__ */ jsx9(Button2, { type: "button", variant: "outline", className: "w-full", onClick: handleSignOut, children: "Sign Out" }),
          /* @__PURE__ */ jsx9(
            Button2,
            {
              type: "button",
              variant: "outline",
              className: "w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20",
              onClick: () => setShowDeleteConfirm(true),
              children: "Delete Account"
            }
          )
        ] })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx9(AlertDialog2, { open: showDeleteConfirm, onOpenChange: setShowDeleteConfirm, children: /* @__PURE__ */ jsxs7(AlertDialogContent2, { children: [
      /* @__PURE__ */ jsxs7(AlertDialogHeader2, { children: [
        /* @__PURE__ */ jsx9(AlertDialogTitle2, { children: "Delete Account" }),
        /* @__PURE__ */ jsx9(AlertDialogDescription2, { children: "This action cannot be undone. This will permanently delete your account and remove all your data from our servers." })
      ] }),
      /* @__PURE__ */ jsxs7(AlertDialogFooter2, { children: [
        /* @__PURE__ */ jsx9(AlertDialogCancel2, { children: "Cancel" }),
        /* @__PURE__ */ jsx9(AlertDialogAction2, { onClick: handleDeleteAccount, children: "Delete Account" })
      ] })
    ] }) })
  ] });
}

// src/components/CollaboratorList.tsx
import { useCallback as useCallback2, useState as useState4 } from "react";

// src/components/ui/LoadingState.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var sizeClasses = {
  sm: "h-4 w-4 border",
  md: "h-8 w-8 border-2",
  lg: "h-12 w-12 border-2"
};
var paddingClasses = {
  sm: "py-2",
  md: "py-8",
  lg: "py-12"
};
function LoadingState({ message, size = "md", className }) {
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      className: cn(
        "flex flex-col items-center justify-center gap-2",
        paddingClasses[size],
        className
      ),
      children: [
        /* @__PURE__ */ jsx10(
          "div",
          {
            className: cn(
              "animate-spin rounded-full border-[var(--jh-primary,#3b82f6)] border-t-transparent",
              sizeClasses[size]
            )
          }
        ),
        message && /* @__PURE__ */ jsx10("span", { className: "text-sm text-[var(--jh-text-secondary,#525252)]", children: message })
      ]
    }
  );
}

// src/components/CollaboratorList.tsx
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var defaultPermissionColors = {
  view: {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-300"
  },
  edit: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-300"
  },
  admin: {
    bg: "bg-purple-100 dark:bg-purple-900/30",
    text: "text-purple-700 dark:text-purple-300"
  }
};
function formatRelativeDate(isoString) {
  const date = new Date(isoString);
  const now = /* @__PURE__ */ new Date();
  const diff = date.getTime() - now.getTime();
  const days = Math.ceil(diff / (1e3 * 60 * 60 * 24));
  if (days < 0) return "Expired";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `${days} days`;
}
function PermissionBadge({
  permission,
  colors = defaultPermissionColors
}) {
  const colorConfig = colors[permission] || {
    bg: "bg-[var(--jh-bg-secondary,#f5f5f5)]",
    text: "text-[var(--jh-text-secondary,#525252)]"
  };
  const labels = {
    view: "View",
    edit: "Edit",
    admin: "Admin"
  };
  return /* @__PURE__ */ jsx11(
    "span",
    {
      className: `px-2 py-0.5 text-xs font-medium rounded ${colorConfig.bg} ${colorConfig.text}`,
      children: labels[permission] || permission
    }
  );
}
function CollaboratorList({
  collaborators,
  pendingInvites,
  onRemoveCollaborator,
  onRevokeInvite,
  onCopyInviteLink,
  isLoading = false,
  error,
  collaboratorsLabel = "Collaborators",
  pendingInvitesLabel = "Pending Invites",
  emptyCollaboratorsMessage = "No collaborators yet",
  emptyInvitesMessage = "No pending invites",
  permissionColors = defaultPermissionColors,
  renderStatus,
  canRemoveCollaborator
}) {
  const [removingId, setRemovingId] = useState4(null);
  const [revokingToken, setRevokingToken] = useState4(null);
  const [copiedToken, setCopiedToken] = useState4(null);
  const handleRemove = useCallback2(
    async (accountId) => {
      if (!onRemoveCollaborator) return;
      setRemovingId(accountId);
      try {
        await onRemoveCollaborator(accountId);
      } finally {
        setRemovingId(null);
      }
    },
    [onRemoveCollaborator]
  );
  const handleRevoke = useCallback2(
    async (token) => {
      if (!onRevokeInvite) return;
      setRevokingToken(token);
      try {
        await onRevokeInvite(token);
      } finally {
        setRevokingToken(null);
      }
    },
    [onRevokeInvite]
  );
  const handleCopyLink = useCallback2(
    async (token) => {
      if (!onCopyInviteLink) return;
      try {
        await onCopyInviteLink(token);
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2e3);
      } catch {
      }
    },
    [onCopyInviteLink]
  );
  if (isLoading) {
    return /* @__PURE__ */ jsx11(LoadingState, {});
  }
  return /* @__PURE__ */ jsxs9("div", { className: "space-y-6", children: [
    error && /* @__PURE__ */ jsx11("div", { className: "rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-900 dark:text-red-300", children: error }),
    /* @__PURE__ */ jsxs9("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsxs9("h3", { className: "text-xs font-medium text-[var(--jh-text-tertiary,#737373)] flex items-center gap-1.5 px-1", children: [
        /* @__PURE__ */ jsx11("span", { children: "\u{1F465}" }),
        collaboratorsLabel,
        " (",
        collaborators.length,
        ")"
      ] }),
      collaborators.length === 0 ? /* @__PURE__ */ jsx11("p", { className: "text-sm text-[var(--jh-text-tertiary,#737373)] py-3 text-center", children: emptyCollaboratorsMessage }) : /* @__PURE__ */ jsx11("div", { className: "space-y-1", children: collaborators.map((collab) => /* @__PURE__ */ jsxs9(
        "div",
        {
          className: "flex items-center justify-between gap-2 py-1.5 px-2 border border-[var(--jh-border-primary,#e5e7eb)] rounded hover:bg-[var(--jh-bg-hover,#f5f5f5)]",
          children: [
            /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [
              /* @__PURE__ */ jsx11("span", { className: "text-sm font-medium text-[var(--jh-text-primary,#171717)] truncate", children: collab.name }),
              /* @__PURE__ */ jsxs9("span", { className: "text-xs text-[var(--jh-text-tertiary,#737373)] truncate", children: [
                "(",
                collab.email,
                ")"
              ] }),
              /* @__PURE__ */ jsx11(PermissionBadge, { permission: collab.permission, colors: permissionColors }),
              renderStatus?.(collab)
            ] }),
            onRemoveCollaborator && canRemoveCollaborator?.(collab) !== false && /* @__PURE__ */ jsx11(
              Button,
              {
                size: "sm",
                variant: "ghost",
                onClick: () => handleRemove(collab.accountId),
                isLoading: removingId === collab.accountId,
                className: "text-[var(--jh-danger,#ef4444)] hover:bg-red-50 dark:hover:bg-red-900/30 h-7 w-7 p-0 shrink-0",
                "aria-label": `Remove ${collab.name}`,
                children: removingId !== collab.accountId && "\u{1F5D1}\uFE0F"
              }
            )
          ]
        },
        collab.accountId
      )) })
    ] }),
    /* @__PURE__ */ jsxs9("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsxs9("h3", { className: "text-xs font-medium text-[var(--jh-text-tertiary,#737373)] flex items-center gap-1.5 px-1", children: [
        /* @__PURE__ */ jsx11("span", { children: "\u2709\uFE0F" }),
        pendingInvitesLabel,
        " (",
        pendingInvites.length,
        ")"
      ] }),
      pendingInvites.length === 0 ? /* @__PURE__ */ jsx11("p", { className: "text-sm text-[var(--jh-text-tertiary,#737373)] py-3 text-center", children: emptyInvitesMessage }) : /* @__PURE__ */ jsx11("div", { className: "space-y-1", children: pendingInvites.map((invite) => /* @__PURE__ */ jsxs9(
        "div",
        {
          className: "flex items-center justify-between gap-2 py-1.5 px-2 border border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 rounded",
          children: [
            /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [
              /* @__PURE__ */ jsx11("span", { className: "text-sm font-medium text-[var(--jh-text-primary,#171717)] truncate", children: invite.recipientEmail }),
              /* @__PURE__ */ jsx11(PermissionBadge, { permission: invite.permission, colors: permissionColors }),
              /* @__PURE__ */ jsxs9("span", { className: "text-xs text-[var(--jh-text-tertiary,#737373)] flex items-center gap-1 shrink-0", children: [
                /* @__PURE__ */ jsx11("span", { children: "\u23F0" }),
                "Expires ",
                invite.expiresAt ? formatRelativeDate(invite.expiresAt) : "never"
              ] })
            ] }),
            /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-1 shrink-0", children: [
              onCopyInviteLink && /* @__PURE__ */ jsx11(
                Button,
                {
                  size: "sm",
                  variant: "ghost",
                  onClick: () => handleCopyLink(invite.token),
                  className: "text-[var(--jh-primary,#3b82f6)] hover:bg-[var(--jh-primary-light,rgba(59,130,246,0.1))] h-7 w-7 p-0",
                  "aria-label": "Copy invite link",
                  children: copiedToken === invite.token ? "\u2713" : "\u{1F4CB}"
                }
              ),
              onRevokeInvite && /* @__PURE__ */ jsx11(
                Button,
                {
                  size: "sm",
                  variant: "ghost",
                  onClick: () => handleRevoke(invite.token),
                  isLoading: revokingToken === invite.token,
                  className: "text-[var(--jh-text-secondary,#525252)] hover:text-[var(--jh-danger,#ef4444)] hover:bg-red-50 dark:hover:bg-red-900/30 h-7 w-7 p-0",
                  "aria-label": "Revoke invite",
                  children: revokingToken !== invite.token && "\u2715"
                }
              )
            ] })
          ]
        },
        invite.token
      )) })
    ] })
  ] });
}

// src/components/ConfirmDialog.tsx
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  onConfirm,
  onCancel
}) {
  return /* @__PURE__ */ jsx12(Dialog, { open, onOpenChange: (v) => !v && onCancel(), children: /* @__PURE__ */ jsxs10(DialogContent, { children: [
    /* @__PURE__ */ jsxs10(DialogHeader, { children: [
      /* @__PURE__ */ jsx12(DialogTitle, { children: title }),
      message && /* @__PURE__ */ jsx12(DialogDescription, { children: message })
    ] }),
    /* @__PURE__ */ jsxs10(DialogFooter, { children: [
      /* @__PURE__ */ jsx12(Button, { variant: "outline", onClick: onCancel, children: cancelText }),
      /* @__PURE__ */ jsx12(Button, { variant: variant === "danger" ? "danger" : "primary", onClick: onConfirm, children: confirmText })
    ] })
  ] }) });
}

// src/components/CreateDialog.tsx
import { useCallback as useCallback3, useEffect as useEffect2, useState as useState5 } from "react";
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
function CreateDialog({
  open,
  onOpenChange,
  onSubmit,
  title = "Create New",
  description,
  inputLabel = "Name",
  placeholder = "Enter name...",
  submitText = "Create",
  cancelText = "Cancel",
  required = true,
  defaultValue = "",
  isSubmitting = false,
  validate
}) {
  const [value, setValue] = useState5(defaultValue);
  const [error, setError] = useState5();
  useEffect2(() => {
    if (open) {
      setValue(defaultValue);
      setError(void 0);
    }
  }, [open, defaultValue]);
  const handleSubmit = useCallback3(
    async (e) => {
      e.preventDefault();
      const trimmedValue = value.trim();
      if (required && !trimmedValue) {
        setError("This field is required");
        return;
      }
      if (validate) {
        const validationError = validate(trimmedValue);
        if (validationError) {
          setError(validationError);
          return;
        }
      }
      setError(void 0);
      try {
        await onSubmit(trimmedValue);
        onOpenChange(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    },
    [value, required, validate, onSubmit, onOpenChange]
  );
  const handleCancel = useCallback3(() => {
    onOpenChange(false);
  }, [onOpenChange]);
  const handleKeyDown = useCallback3(
    (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleCancel]
  );
  return /* @__PURE__ */ jsx13(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsx13(DialogContent, { onKeyDown: handleKeyDown, children: /* @__PURE__ */ jsxs11("form", { onSubmit: handleSubmit, children: [
    /* @__PURE__ */ jsxs11(DialogHeader, { children: [
      /* @__PURE__ */ jsx13(DialogTitle, { children: title }),
      description && /* @__PURE__ */ jsx13(DialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsx13("div", { className: "py-4", children: /* @__PURE__ */ jsxs11("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsx13(Label, { htmlFor: "create-dialog-input", required, children: inputLabel }),
      /* @__PURE__ */ jsx13(
        Input,
        {
          id: "create-dialog-input",
          value,
          onChange: (e) => {
            setValue(e.target.value);
            if (error) setError(void 0);
          },
          placeholder,
          autoFocus: true,
          disabled: isSubmitting,
          state: error ? "error" : "default",
          "aria-describedby": error ? "create-dialog-error" : void 0
        }
      ),
      error && /* @__PURE__ */ jsx13("p", { id: "create-dialog-error", className: "text-sm text-[var(--jh-danger,#ef4444)]", children: error })
    ] }) }),
    /* @__PURE__ */ jsxs11(DialogFooter, { children: [
      /* @__PURE__ */ jsx13(
        Button,
        {
          type: "button",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
          children: cancelText
        }
      ),
      /* @__PURE__ */ jsx13(Button, { type: "submit", isLoading: isSubmitting, children: submitText })
    ] })
  ] }) }) });
}

// src/components/MessageDialog.tsx
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
function MessageDialog({
  open,
  title,
  message,
  confirmText = "OK",
  onConfirm
}) {
  return /* @__PURE__ */ jsx14(Dialog, { open, onOpenChange: (v) => !v && onConfirm(), children: /* @__PURE__ */ jsxs12(DialogContent, { children: [
    /* @__PURE__ */ jsxs12(DialogHeader, { children: [
      /* @__PURE__ */ jsx14(DialogTitle, { children: title }),
      message && /* @__PURE__ */ jsx14(DialogDescription, { children: message })
    ] }),
    /* @__PURE__ */ jsx14(DialogFooter, { children: /* @__PURE__ */ jsx14(Button, { onClick: onConfirm, children: confirmText }) })
  ] }) });
}

// src/components/SimpleInputDialog.tsx
import { useEffect as useEffect3, useRef, useState as useState6 } from "react";
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
function SimpleInputDialog({
  open,
  title,
  description,
  inputLabel,
  defaultValue = "",
  placeholder,
  confirmText = "Save",
  cancelText = "Cancel",
  required = true,
  onSubmit,
  onCancel
}) {
  const [value, setValue] = useState6(defaultValue);
  const inputRef = useRef(null);
  useEffect3(() => {
    setValue(defaultValue);
  }, [defaultValue, open]);
  useEffect3(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [open]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (required && !value.trim()) return;
    onSubmit(value.trim());
  };
  return /* @__PURE__ */ jsx15(Dialog, { open, onOpenChange: (v) => !v && onCancel(), children: /* @__PURE__ */ jsxs13(DialogContent, { children: [
    /* @__PURE__ */ jsxs13(DialogHeader, { children: [
      /* @__PURE__ */ jsx15(DialogTitle, { children: title }),
      description && /* @__PURE__ */ jsx15(DialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs13("form", { onSubmit: handleSubmit, children: [
      inputLabel && /* @__PURE__ */ jsx15("label", { className: "mb-2 block text-sm font-medium", children: inputLabel }),
      /* @__PURE__ */ jsx15(
        Input,
        {
          ref: inputRef,
          value,
          onChange: (e) => setValue(e.target.value),
          placeholder
        }
      ),
      /* @__PURE__ */ jsxs13(DialogFooter, { className: "mt-4", children: [
        /* @__PURE__ */ jsx15(Button, { type: "button", variant: "outline", onClick: onCancel, children: cancelText }),
        /* @__PURE__ */ jsx15(Button, { type: "submit", children: confirmText })
      ] })
    ] })
  ] }) });
}

// src/components/DraggableTreeNode.tsx
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { getNodeId as getNodeId2 } from "@jbr-jazz/hierarchy-shared";
import {
  useCallback as useCallback4,
  useMemo as useMemo3,
  useState as useState7
} from "react";

// src/components/ui/ContextMenu.tsx
import { useEffect as useEffect4 } from "react";
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
function ContextMenu({ x, y, actions, onClose, className }) {
  useEffect4(() => {
    const handler = () => onClose();
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [onClose]);
  useEffect4(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
  return /* @__PURE__ */ jsx16(
    "div",
    {
      role: "menu",
      className: `
        fixed z-[1000] min-w-[150px] rounded-md
        border border-[var(--jh-border-primary,#e5e7eb)]
        bg-[var(--jh-bg-elevated,#ffffff)]
        shadow-lg py-1
        ${className || ""}
      `,
      style: { top: y, left: x },
      children: actions.map((action) => /* @__PURE__ */ jsxs14(
        "button",
        {
          type: "button",
          role: "menuitem",
          onClick: (e) => {
            e.stopPropagation();
            action.onAction();
          },
          disabled: action.disabled,
          className: `
            flex items-center w-full px-3 py-2 text-left text-sm
            ${action.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-[var(--jh-bg-hover,#f5f5f5)]"}
            ${action.destructive ? "text-[var(--jh-danger,#ef4444)]" : "text-[var(--jh-text-primary,#171717)]"}
          `,
          children: [
            action.icon && /* @__PURE__ */ jsx16("span", { className: "mr-2", children: action.icon }),
            /* @__PURE__ */ jsx16("span", { children: action.label }),
            action.shortcut && /* @__PURE__ */ jsx16("span", { className: "ml-auto opacity-50 text-xs", children: action.shortcut })
          ]
        },
        action.id
      ))
    }
  );
}

// src/components/ui/DropdownMenu.tsx
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as React3 from "react";
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
var DropdownMenuContent = React3.forwardRef(({ className = "", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx17(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border border-[var(--jh-border-primary,#e5e7eb)] bg-[var(--jh-bg-elevated,#ffffff)] p-1 shadow-md",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = React3.forwardRef(({ className = "", destructive, ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-[var(--jh-bg-hover,#f5f5f5)] focus:bg-[var(--jh-bg-hover,#f5f5f5)]",
      destructive && "text-[var(--jh-danger,#ef4444)]",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuSeparator = React3.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-[var(--jh-border-primary,#e5e7eb)]", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var DropdownMenuLabel = React3.forwardRef(({ className = "", inset, ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold text-[var(--jh-text-primary,#171717)]",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSubTrigger = React3.forwardRef(({ className = "", inset, children, ...props }, ref) => /* @__PURE__ */ jsxs15(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-[var(--jh-bg-hover,#f5f5f5)] focus:bg-[var(--jh-bg-hover,#f5f5f5)]",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx17("span", { className: "ml-auto", children: "\u25B8" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = React3.forwardRef(({ className = "", ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border border-[var(--jh-border-primary,#e5e7eb)] bg-[var(--jh-bg-elevated,#ffffff)] p-1 shadow-lg",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

// src/components/ui/EmptyState.tsx
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
function EmptyState({
  message = "No items to display",
  icon,
  action,
  className,
  children
}) {
  if (children) {
    return /* @__PURE__ */ jsx18("div", { className: cn("p-6 text-center text-[var(--jh-text-secondary,#525252)]", className), children });
  }
  return /* @__PURE__ */ jsxs16(
    "div",
    {
      className: cn("flex flex-col items-center justify-center gap-3 p-6 text-center", className),
      children: [
        icon && /* @__PURE__ */ jsx18("div", { className: "text-[var(--jh-text-secondary,#525252)]", children: icon }),
        /* @__PURE__ */ jsx18("p", { className: "text-sm text-[var(--jh-text-secondary,#525252)]", children: message }),
        action && /* @__PURE__ */ jsx18("div", { className: "mt-2", children: action })
      ]
    }
  );
}

// src/components/DraggableTreeNode.tsx
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
function DraggableTreeNode({
  node,
  depth,
  isSelected = false,
  onSelect,
  onToggle,
  actions = [],
  renderIcon,
  renderLabel,
  children,
  className,
  draggable = true,
  droppable = true,
  onStartEdit
}) {
  const [isHovered, setIsHovered] = useState7(false);
  const [contextMenu, setContextMenu] = useState7(null);
  const isFolder = node.type === "folder";
  const folder = isFolder ? node : null;
  const isExpanded = folder?.expanded ?? false;
  const nodeId = getNodeId2(node);
  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    isDragging
  } = useDraggable({
    id: nodeId,
    data: {
      type: "tree-node",
      node,
      nodeId,
      isFolder
    },
    disabled: !draggable
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${nodeId}`,
    data: {
      type: "folder",
      node,
      nodeId,
      accepts: ["tree-node"]
    },
    disabled: !droppable || !isFolder
  });
  const setNodeRef = useCallback4(
    (element) => {
      setDragRef(element);
      if (isFolder && droppable) {
        setDropRef(element);
      }
    },
    [setDragRef, setDropRef, isFolder, droppable]
  );
  const handleClick = useCallback4(() => {
    onSelect?.(node);
  }, [node, onSelect]);
  const handleToggle = useCallback4(
    (e) => {
      e.stopPropagation();
      if (folder && onToggle) {
        onToggle(folder);
      }
    },
    [folder, onToggle]
  );
  const handleContextMenu = useCallback4((e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);
  const closeContextMenu = useCallback4(() => {
    setContextMenu(null);
  }, []);
  const nodeData = useMemo3(() => buildActionNodeData(node, folder), [node, folder]);
  const handleAction = useCallback4(
    (action) => {
      action.onAction(nodeData);
      closeContextMenu();
    },
    [nodeData, closeContextMenu]
  );
  const contextMenuActions = useMemo3(() => {
    return actions.map((action) => ({
      id: action.id,
      label: action.label,
      icon: action.icon,
      shortcut: action.shortcut,
      disabled: action.disabled,
      destructive: action.destructive,
      onAction: () => handleAction(action)
    }));
  }, [actions, handleAction]);
  const handleKeyDown = useCallback4(
    (e) => {
      switch (e.key) {
        case "Enter":
          e.preventDefault();
          onSelect?.(node);
          break;
        case " ":
          e.preventDefault();
          if (isFolder && onToggle && folder) {
            onToggle(folder);
          }
          break;
        case "ArrowRight":
          if (isFolder && folder && !folder.expanded && onToggle) {
            e.preventDefault();
            onToggle(folder);
          }
          break;
        case "ArrowLeft":
          if (isFolder && folder && folder.expanded && onToggle) {
            e.preventDefault();
            onToggle(folder);
          }
          break;
        case "F2":
          e.preventDefault();
          onStartEdit?.(node);
          break;
      }
    },
    [node, isFolder, folder, onSelect, onToggle, onStartEdit]
  );
  const defaultIcon = isFolder ? isExpanded ? "\u{1F4C2}" : "\u{1F4C1}" : "\u{1F4C4}";
  const rowActions = actions.slice(0, 2);
  return /* @__PURE__ */ jsxs17("div", { className, children: [
    /* @__PURE__ */ jsxs17(
      "div",
      {
        ref: setNodeRef,
        ...dragAttributes,
        ...dragListeners,
        role: "treeitem",
        "aria-selected": isSelected,
        "aria-expanded": isFolder ? isExpanded : void 0,
        tabIndex: 0,
        style: {
          paddingLeft: `${8 + depth * 16}px`,
          paddingRight: "8px",
          paddingTop: "4px",
          paddingBottom: "4px",
          opacity: isDragging ? 0.5 : 1
        },
        className: `
          flex items-center cursor-pointer rounded select-none outline-none
          transition-colors duration-150
          ${isSelected ? "bg-[var(--jh-primary-light,rgba(59,130,246,0.1))]" : isOver ? "bg-[var(--jh-primary-light,rgba(59,130,246,0.05))] ring-2 ring-[var(--jh-primary,#3b82f6)] ring-inset" : isHovered ? "bg-[var(--jh-bg-hover,#f5f5f5)]" : ""}
          focus-visible:ring-2 focus-visible:ring-[var(--jh-primary,#3b82f6)] focus-visible:ring-inset
        `,
        onClick: handleClick,
        onContextMenu: handleContextMenu,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        onKeyDown: handleKeyDown,
        children: [
          isFolder && /* @__PURE__ */ jsx19(
            "button",
            {
              type: "button",
              onClick: handleToggle,
              className: "mr-1 p-0.5 text-[var(--jh-text-tertiary,#737373)] hover:text-[var(--jh-text-primary,#171717)]",
              "aria-label": isExpanded ? "Collapse folder" : "Expand folder",
              tabIndex: -1,
              children: isExpanded ? "\u25BC" : "\u25B6"
            }
          ),
          /* @__PURE__ */ jsx19("span", { className: "w-4 h-4 mr-2 flex-shrink-0 flex items-center justify-center", children: renderIcon ? renderIcon(node) : defaultIcon }),
          /* @__PURE__ */ jsx19("span", { className: "flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-[var(--jh-text-primary,#171717)]", children: renderLabel ? renderLabel(node) : node.name }),
          isHovered && rowActions.length > 0 && /* @__PURE__ */ jsx19("div", { className: "flex gap-1 ml-2", children: rowActions.map((action) => /* @__PURE__ */ jsx19(
            "button",
            {
              type: "button",
              onClick: (e) => {
                e.stopPropagation();
                handleAction(action);
              },
              className: `
                  p-1 border-none bg-transparent cursor-pointer rounded text-xs
                  ${action.destructive ? "text-[var(--jh-danger,#ef4444)] hover:bg-red-50" : "text-[var(--jh-text-secondary,#525252)] hover:bg-[var(--jh-bg-hover,#f5f5f5)]"}
                `,
              disabled: action.disabled,
              title: action.label,
              "aria-label": action.label,
              tabIndex: -1,
              children: action.icon || action.label
            },
            action.id
          )) })
        ]
      }
    ),
    isFolder && isExpanded && children && /* @__PURE__ */ jsx19("div", { role: "group", "aria-label": `Contents of ${node.name}`, children }),
    contextMenu && contextMenuActions.length > 0 && /* @__PURE__ */ jsx19(
      ContextMenu,
      {
        x: contextMenu.x,
        y: contextMenu.y,
        actions: contextMenuActions,
        onClose: closeContextMenu
      }
    )
  ] });
}

// src/components/DraggableTreeView.tsx
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  getNodeId as getNodeId3
} from "@jbr-jazz/hierarchy-shared";
import React5, { useCallback as useCallback5, useMemo as useMemo4, useState as useState8 } from "react";

// src/components/ReorderDropZone.tsx
import { useDroppable as useDroppable2 } from "@dnd-kit/core";
import { jsx as jsx20 } from "react/jsx-runtime";
function ReorderDropZone({
  id,
  beforeItemId,
  afterItemId,
  parentId,
  insertIndex,
  isDragging,
  depth = 0
}) {
  const { setNodeRef, isOver } = useDroppable2({
    id,
    data: {
      type: "reorder-zone",
      beforeItemId,
      afterItemId,
      parentId,
      insertIndex
    }
  });
  if (!isDragging) {
    return null;
  }
  return /* @__PURE__ */ jsx20(
    "div",
    {
      ref: setNodeRef,
      className: `
        transition-all duration-150
        ${isOver ? "h-1 bg-[var(--jh-primary,#3b82f6)] rounded" : "h-px bg-transparent hover:bg-[var(--jh-border-primary,#e5e7eb)]"}
      `,
      style: {
        marginLeft: `${8 + depth * 16}px`,
        marginRight: "8px"
      },
      "aria-hidden": "true"
    }
  );
}

// src/components/DraggableTreeView.tsx
import { Fragment as Fragment3, jsx as jsx21, jsxs as jsxs18 } from "react/jsx-runtime";
function findNodeById(folders, id) {
  for (const folder of folders) {
    if (getNodeId3(folder) === id) return folder;
    const children = folder.children;
    if (children) {
      const found = findNodeById([...children].filter(Boolean), id);
      if (found) return found;
    }
  }
  return null;
}
function DraggableTreeView({
  folders,
  selectedId,
  onSelect,
  onToggle,
  getActions,
  renderIcon,
  renderLabel,
  emptyContent,
  isLoading,
  className,
  onMoveToFolder,
  onReorder,
  enableDragDrop = true,
  renderDragOverlay
}) {
  const [activeNode, setActiveNode] = useState8(null);
  const [isDragging, setIsDragging] = useState8(false);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8
        // 8px movement before drag starts
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        // 250ms hold before drag
        tolerance: 8
        // Allow 8px movement during delay
      }
    })
  );
  const handleDragStart = useCallback5(
    (event) => {
      const { active } = event;
      const node = findNodeById(folders, active.id);
      setActiveNode(node);
      setIsDragging(true);
    },
    [folders]
  );
  const handleDragOver = useCallback5((_event) => {
  }, []);
  const handleDragEnd = useCallback5(
    (event) => {
      const { over } = event;
      setActiveNode(null);
      setIsDragging(false);
      if (!over || !activeNode) return;
      const overData = over.data.current;
      if (overData?.type === "reorder-zone") {
        const { parentId, insertIndex } = overData;
        onReorder?.(activeNode, parentId, insertIndex);
        return;
      }
      if (overData?.type === "folder") {
        const targetNode = overData.node;
        if (getNodeId3(targetNode) === getNodeId3(activeNode)) {
          return;
        }
        onMoveToFolder?.(activeNode, targetNode);
        return;
      }
    },
    [activeNode, onReorder, onMoveToFolder]
  );
  const handleDragCancel = useCallback5(() => {
    setActiveNode(null);
    setIsDragging(false);
  }, []);
  const defaultDragOverlay = useMemo4(() => {
    if (!activeNode) return null;
    const icon = activeNode.type === "folder" ? "\u{1F4C1}" : "\u{1F4C4}";
    return /* @__PURE__ */ jsxs18("div", { className: "flex items-center gap-2 px-3 py-2 bg-[var(--jh-bg-elevated,#ffffff)] border border-[var(--jh-border-primary,#e5e7eb)] rounded shadow-lg", children: [
      /* @__PURE__ */ jsx21("span", { children: icon }),
      /* @__PURE__ */ jsx21("span", { className: "text-sm", children: activeNode.name })
    ] });
  }, [activeNode]);
  const containerClass = `font-sans text-sm ${className || ""}`.trim();
  if (isLoading) {
    return /* @__PURE__ */ jsx21("div", { className: containerClass, children: /* @__PURE__ */ jsx21(LoadingState, { message: "Loading..." }) });
  }
  if (folders.length === 0) {
    return /* @__PURE__ */ jsx21("div", { className: containerClass, children: emptyContent || /* @__PURE__ */ jsx21(EmptyState, {}) });
  }
  const treeContent = /* @__PURE__ */ jsxs18("div", { className: containerClass, role: "tree", "aria-label": "Folder tree", children: [
    enableDragDrop && /* @__PURE__ */ jsx21(
      ReorderDropZone,
      {
        id: "drop-zone-root-start",
        beforeItemId: null,
        afterItemId: folders[0] ? getNodeId3(folders[0]) : null,
        parentId: null,
        insertIndex: 0,
        isDragging,
        depth: 0
      }
    ),
    folders.map((folder, index) => /* @__PURE__ */ jsxs18(React5.Fragment, { children: [
      /* @__PURE__ */ jsx21(
        RecursiveTreeNode,
        {
          node: folder,
          depth: 0,
          selectedId,
          onSelect,
          onToggle,
          getActions,
          renderIcon,
          renderLabel,
          enableDragDrop,
          isDragging
        }
      ),
      enableDragDrop && /* @__PURE__ */ jsx21(
        ReorderDropZone,
        {
          id: `drop-zone-root-${index + 1}`,
          beforeItemId: getNodeId3(folder),
          afterItemId: folders[index + 1] ? getNodeId3(folders[index + 1]) : null,
          parentId: null,
          insertIndex: index + 1,
          isDragging,
          depth: 0
        }
      )
    ] }, getNodeId3(folder)))
  ] });
  if (!enableDragDrop) {
    return treeContent;
  }
  return /* @__PURE__ */ jsxs18(
    DndContext,
    {
      sensors,
      collisionDetection: closestCenter,
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDragEnd: handleDragEnd,
      onDragCancel: handleDragCancel,
      children: [
        treeContent,
        /* @__PURE__ */ jsx21(DragOverlay, { children: activeNode && (renderDragOverlay ? renderDragOverlay(activeNode) : defaultDragOverlay) })
      ]
    }
  );
}
function RecursiveTreeNode({
  node,
  depth,
  selectedId,
  onSelect,
  onToggle,
  getActions,
  renderIcon,
  renderLabel,
  enableDragDrop = true,
  isDragging = false
}) {
  const nodeId = getNodeId3(node);
  const isSelected = selectedId === nodeId;
  const actions = getActions ? getActions(node) : [];
  const isFolder = node.type === "folder";
  const folder = isFolder ? node : null;
  const childrenList = folder?.children;
  const children = childrenList ? [...childrenList].filter((c) => c !== null && c !== void 0) : [];
  return /* @__PURE__ */ jsx21(
    DraggableTreeNode,
    {
      node,
      depth,
      isSelected,
      onSelect,
      onToggle,
      actions,
      renderIcon,
      renderLabel,
      draggable: enableDragDrop,
      droppable: enableDragDrop && isFolder,
      children: children.length > 0 && /* @__PURE__ */ jsxs18(Fragment3, { children: [
        enableDragDrop && /* @__PURE__ */ jsx21(
          ReorderDropZone,
          {
            id: `drop-zone-${nodeId}-start`,
            beforeItemId: null,
            afterItemId: children[0] ? getNodeId3(children[0]) : null,
            parentId: nodeId,
            insertIndex: 0,
            isDragging,
            depth: depth + 1
          }
        ),
        children.map((child, index) => /* @__PURE__ */ jsxs18(React5.Fragment, { children: [
          /* @__PURE__ */ jsx21(
            RecursiveTreeNode,
            {
              node: child,
              depth: depth + 1,
              selectedId,
              onSelect,
              onToggle,
              getActions,
              renderIcon,
              renderLabel,
              enableDragDrop,
              isDragging
            }
          ),
          enableDragDrop && /* @__PURE__ */ jsx21(
            ReorderDropZone,
            {
              id: `drop-zone-${nodeId}-${index + 1}`,
              beforeItemId: getNodeId3(child),
              afterItemId: children[index + 1] ? getNodeId3(children[index + 1]) : null,
              parentId: nodeId,
              insertIndex: index + 1,
              isDragging,
              depth: depth + 1
            }
          )
        ] }, getNodeId3(child)))
      ] })
    }
  );
}

// src/components/MoveDialog.tsx
import { getNodeId as getNodeId4 } from "@jbr-jazz/hierarchy-shared";
import { useCallback as useCallback6, useMemo as useMemo5, useState as useState9 } from "react";
import { jsx as jsx22, jsxs as jsxs19 } from "react/jsx-runtime";
function getDisabledIds(source) {
  const disabled = /* @__PURE__ */ new Set();
  function collect(node) {
    disabled.add(getNodeId4(node));
    const children = node.children;
    if (children) {
      for (const child of children) {
        if (child && child.type === "folder") {
          collect(child);
        }
      }
    }
  }
  collect(source);
  return disabled;
}
function FolderTreeItem({ folder, depth, selectedId, disabledIds, onSelect }) {
  const [isExpanded, setIsExpanded] = useState9(true);
  const nodeId = getNodeId4(folder);
  const isSelected = selectedId === nodeId;
  const isDisabled = disabledIds.has(nodeId);
  const childFolders = useMemo5(() => {
    const children = folder.children;
    if (!children) return [];
    return [...children].filter(
      (c) => c !== null && c !== void 0 && c.type === "folder"
    );
  }, [folder.children]);
  const hasChildren = childFolders.length > 0;
  return /* @__PURE__ */ jsxs19("div", { children: [
    /* @__PURE__ */ jsxs19(
      "div",
      {
        role: "treeitem",
        "aria-selected": isSelected,
        "aria-disabled": isDisabled,
        "aria-expanded": hasChildren ? isExpanded : void 0,
        className: `
          flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer select-none
          ${isSelected ? "bg-[var(--jh-primary-light,rgba(59,130,246,0.1))] border border-[var(--jh-primary,#3b82f6)]" : "hover:bg-[var(--jh-bg-hover,#f5f5f5)]"}
          ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}
        `,
        style: { paddingLeft: `${8 + depth * 20}px` },
        onClick: () => {
          if (!isDisabled) {
            onSelect(folder);
          }
        },
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!isDisabled) {
              onSelect(folder);
            }
          }
        },
        tabIndex: isDisabled ? -1 : 0,
        children: [
          hasChildren ? /* @__PURE__ */ jsx22(
            "button",
            {
              type: "button",
              onClick: (e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              },
              className: "text-[var(--jh-text-tertiary,#737373)] hover:text-[var(--jh-text-primary,#171717)]",
              "aria-label": isExpanded ? "Collapse" : "Expand",
              children: isExpanded ? "\u25BC" : "\u25B6"
            }
          ) : /* @__PURE__ */ jsx22("span", { className: "w-4" }),
          /* @__PURE__ */ jsx22("span", { children: isExpanded ? "\u{1F4C2}" : "\u{1F4C1}" }),
          /* @__PURE__ */ jsx22("span", { className: "text-sm text-[var(--jh-text-primary,#171717)] truncate", children: folder.name }),
          isDisabled && /* @__PURE__ */ jsx22("span", { className: "text-xs text-[var(--jh-text-tertiary,#737373)]", children: "(source)" })
        ]
      }
    ),
    hasChildren && isExpanded && /* @__PURE__ */ jsx22("div", { role: "group", children: childFolders.map((child) => /* @__PURE__ */ jsx22(
      FolderTreeItem,
      {
        folder: child,
        depth: depth + 1,
        selectedId,
        disabledIds,
        onSelect
      },
      getNodeId4(child)
    )) })
  ] });
}
function MoveDialog({
  open,
  onOpenChange,
  onMove,
  sourceNode,
  folders,
  title = "Move to",
  description,
  moveText = "Move",
  cancelText = "Cancel",
  rootLabel = "Root (top level)",
  isMoving = false
}) {
  const [selectedFolder, setSelectedFolder] = useState9(null);
  const [isRootSelected, setIsRootSelected] = useState9(false);
  const disabledIds = useMemo5(() => getDisabledIds(sourceNode), [sourceNode]);
  const rootFolders = useMemo5(() => {
    return folders.filter((f) => f.type === "folder");
  }, [folders]);
  const handleSelectFolder = useCallback6((folder) => {
    setSelectedFolder(folder);
    setIsRootSelected(false);
  }, []);
  const handleSelectRoot = useCallback6(() => {
    setSelectedFolder(null);
    setIsRootSelected(true);
  }, []);
  const handleMove = useCallback6(async () => {
    if (!isRootSelected && !selectedFolder) return;
    try {
      await onMove(selectedFolder);
      onOpenChange(false);
    } catch (err) {
      console.error("Move failed:", err);
    }
  }, [isRootSelected, selectedFolder, onMove, onOpenChange]);
  const handleCancel = useCallback6(() => {
    onOpenChange(false);
    setSelectedFolder(null);
    setIsRootSelected(false);
  }, [onOpenChange]);
  const currentLocationText = useMemo5(() => {
    const parent = sourceNode.parent;
    if (!parent) return "Root";
    return parent.name;
  }, [sourceNode.parent]);
  return /* @__PURE__ */ jsx22(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs19(DialogContent, { className: "sm:max-w-[450px]", children: [
    /* @__PURE__ */ jsxs19(DialogHeader, { children: [
      /* @__PURE__ */ jsx22(DialogTitle, { children: title }),
      description && /* @__PURE__ */ jsx22(DialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs19("div", { className: "py-4 space-y-4", children: [
      /* @__PURE__ */ jsxs19("div", { className: "text-sm text-[var(--jh-text-secondary,#525252)]", children: [
        "Moving ",
        /* @__PURE__ */ jsx22("strong", { children: sourceNode.name }),
        " from ",
        /* @__PURE__ */ jsx22("strong", { children: currentLocationText })
      ] }),
      /* @__PURE__ */ jsxs19(
        "div",
        {
          role: "tree",
          className: "max-h-[300px] overflow-y-auto border border-[var(--jh-border-primary,#e5e7eb)] rounded-md p-2",
          children: [
            /* @__PURE__ */ jsxs19(
              "div",
              {
                role: "treeitem",
                "aria-selected": isRootSelected,
                className: `
                flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer select-none
                ${isRootSelected ? "bg-[var(--jh-primary-light,rgba(59,130,246,0.1))] border border-[var(--jh-primary,#3b82f6)]" : "hover:bg-[var(--jh-bg-hover,#f5f5f5)]"}
              `,
                onClick: handleSelectRoot,
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectRoot();
                  }
                },
                tabIndex: 0,
                children: [
                  /* @__PURE__ */ jsx22("span", { className: "w-4" }),
                  /* @__PURE__ */ jsx22("span", { children: "\u{1F3E0}" }),
                  /* @__PURE__ */ jsx22("span", { className: "text-sm text-[var(--jh-text-primary,#171717)]", children: rootLabel })
                ]
              }
            ),
            rootFolders.map((folder) => /* @__PURE__ */ jsx22(
              FolderTreeItem,
              {
                folder,
                depth: 0,
                selectedId: selectedFolder ? getNodeId4(selectedFolder) : null,
                disabledIds,
                onSelect: handleSelectFolder
              },
              getNodeId4(folder)
            ))
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs19(DialogFooter, { children: [
      /* @__PURE__ */ jsx22(Button, { type: "button", variant: "secondary", onClick: handleCancel, disabled: isMoving, children: cancelText }),
      /* @__PURE__ */ jsx22(
        Button,
        {
          type: "button",
          onClick: handleMove,
          disabled: !isRootSelected && !selectedFolder,
          isLoading: isMoving,
          children: moveText
        }
      )
    ] })
  ] }) });
}

// src/components/sharing/InviteRow.tsx
import { useId } from "react";
import { jsx as jsx23, jsxs as jsxs20 } from "react/jsx-runtime";
var DEFAULT_PERMISSIONS = ["view", "edit", "admin"];
var DEFAULT_PERMISSION_LABELS = {
  view: "View",
  edit: "Edit",
  admin: "Admin"
};
var DEFAULT_EXPIRATIONS = [1, 7, 14, 30];
var MAX_EXPIRATION_DAYS = 30;
var selectClass = "h-10 rounded-md border border-[var(--jh-border-primary,#e5e7eb)] bg-[var(--jh-bg-elevated,#ffffff)] px-3 text-sm text-[var(--jh-text-primary,#171717)] focus:outline-none focus:border-[var(--jh-primary,#3b82f6)]";
function expirationLabel(days) {
  return days === 1 ? "1 day" : `${days} days`;
}
function InviteRow({
  recipient,
  onRecipientChange,
  permission,
  onPermissionChange,
  expiresInDays,
  onExpiresChange,
  permissions = DEFAULT_PERMISSIONS,
  permissionLabels = DEFAULT_PERMISSION_LABELS,
  expirations = DEFAULT_EXPIRATIONS,
  extraFields,
  recipientPlaceholder = "person@example.com",
  error,
  disabled = false,
  onEnter,
  hidePermission = false
}) {
  const uid = useId();
  const permId = `${uid}-permission`;
  const expiresId = `${uid}-expires`;
  const cappedExpirations = expirations.filter((d) => d <= MAX_EXPIRATION_DAYS);
  return /* @__PURE__ */ jsxs20("div", { className: "space-y-2", children: [
    extraFields,
    /* @__PURE__ */ jsx23(
      Input,
      {
        type: "email",
        value: recipient,
        placeholder: recipientPlaceholder,
        disabled,
        state: error ? "error" : "default",
        onChange: (e) => onRecipientChange(e.target.value),
        onKeyDown: (e) => {
          if (e.key === "Enter") onEnter?.();
        }
      }
    ),
    /* @__PURE__ */ jsxs20("div", { className: "flex gap-2", children: [
      !hidePermission && /* @__PURE__ */ jsxs20("div", { className: "flex-1", children: [
        /* @__PURE__ */ jsx23(Label, { htmlFor: permId, children: "Permission" }),
        /* @__PURE__ */ jsx23(
          "select",
          {
            id: permId,
            className: `${selectClass} w-full`,
            value: permission,
            disabled,
            onChange: (e) => onPermissionChange(e.target.value),
            children: permissions.map((p) => /* @__PURE__ */ jsx23("option", { value: p, children: permissionLabels[p] ?? p }, p))
          }
        )
      ] }),
      /* @__PURE__ */ jsxs20("div", { className: "flex-1", children: [
        /* @__PURE__ */ jsx23(Label, { htmlFor: expiresId, children: "Expires" }),
        /* @__PURE__ */ jsx23(
          "select",
          {
            id: expiresId,
            "aria-label": "Expires",
            className: `${selectClass} w-full`,
            value: Math.min(expiresInDays, MAX_EXPIRATION_DAYS),
            disabled,
            onChange: (e) => onExpiresChange(Number(e.target.value)),
            children: cappedExpirations.map((d) => /* @__PURE__ */ jsx23("option", { value: d, children: expirationLabel(d) }, d))
          }
        )
      ] })
    ] }),
    error && /* @__PURE__ */ jsx23("p", { className: "text-xs text-[var(--jh-danger,#ef4444)]", children: error })
  ] });
}

// src/components/sharing/ShareDeliveryButtons.tsx
import { useCallback as useCallback7, useState as useState10 } from "react";
import { jsx as jsx24, jsxs as jsxs21 } from "react/jsx-runtime";
function detectWebShare() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}
function ShareDeliveryButtons({
  onDeliver,
  canDeliver = true,
  isDelivering = false,
  hasWebShare = detectWebShare(),
  copyLabel = "Copy link",
  emailLabel = "Email invite",
  shareLabel = "Share"
}) {
  const [copied, setCopied] = useState10(false);
  const disabled = !canDeliver || isDelivering;
  const handle = useCallback7(
    async (channel) => {
      try {
        await onDeliver(channel);
        if (channel === "copy") {
          setCopied(true);
          setTimeout(() => setCopied(false), 2e3);
        }
      } catch {
      }
    },
    [onDeliver]
  );
  return /* @__PURE__ */ jsxs21("div", { className: "flex gap-2", children: [
    /* @__PURE__ */ jsx24(Button, { variant: "secondary", disabled, onClick: () => handle("copy"), children: copied ? "Copied!" : copyLabel }),
    hasWebShare ? /* @__PURE__ */ jsx24(Button, { variant: "primary", disabled, onClick: () => handle("share"), children: shareLabel }) : /* @__PURE__ */ jsx24(Button, { variant: "primary", disabled, onClick: () => handle("email"), children: emailLabel })
  ] });
}

// src/components/sharing/SharePanel.tsx
import { useCallback as useCallback8, useState as useState11 } from "react";
import { jsx as jsx25, jsxs as jsxs22 } from "react/jsx-runtime";
var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function defaultValidate(recipient) {
  return EMAIL_RE.test(recipient.trim()) ? null : "Enter a valid email address";
}
function defaultBuildInviteUrl(token) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/invite/${token}`;
}
function SharePanel({
  onCreateInvite,
  collaborators,
  pendingInvites,
  onRemoveCollaborator,
  canRemoveCollaborator,
  onRevokeInvite,
  isLoading = false,
  permissions,
  permissionLabels,
  expirations,
  defaultPermission = "edit",
  defaultExpiresInDays = 7,
  recipientPlaceholder,
  inviteExtraFields,
  validateRecipient = defaultValidate,
  buildInviteUrl = defaultBuildInviteUrl,
  webShareTitle = "Invitation",
  hasWebShare,
  renderMemberStatus,
  permissionColors,
  successMessageFor,
  children,
  hideInvitePermission
}) {
  const [recipient, setRecipient] = useState11("");
  const [permission, setPermission] = useState11(defaultPermission);
  const [expiresInDays, setExpiresInDays] = useState11(
    Math.min(defaultExpiresInDays, MAX_EXPIRATION_DAYS)
  );
  const [error, setError] = useState11(void 0);
  const [success, setSuccess] = useState11(void 0);
  const [isDelivering, setIsDelivering] = useState11(false);
  const deliver = useCallback8(
    async (channel) => {
      const validationError = validateRecipient(recipient);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(void 0);
      setSuccess(void 0);
      setIsDelivering(true);
      try {
        const sendEmail = channel === "email";
        const { shareUrl } = await onCreateInvite({
          recipient: recipient.trim(),
          permission,
          expiresInDays,
          sendEmail
        });
        if (channel === "copy") {
          await navigator.clipboard.writeText(shareUrl);
        } else if (channel === "share" && typeof navigator.share === "function") {
          try {
            await navigator.share({ title: webShareTitle, url: shareUrl });
          } catch (shareErr) {
            if (!(shareErr instanceof DOMException && shareErr.name === "AbortError")) {
              throw shareErr;
            }
          }
        }
        setSuccess(
          successMessageFor?.({ recipient: recipient.trim(), sendEmail }) ?? (sendEmail ? `Invite emailed to ${recipient.trim()}` : "Invite link ready")
        );
        setRecipient("");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to create invite");
      } finally {
        setIsDelivering(false);
      }
    },
    [
      recipient,
      permission,
      expiresInDays,
      onCreateInvite,
      validateRecipient,
      webShareTitle,
      successMessageFor
    ]
  );
  const handleCopyInviteLink = useCallback8(
    async (token) => {
      await navigator.clipboard.writeText(buildInviteUrl(token));
    },
    [buildInviteUrl]
  );
  return /* @__PURE__ */ jsxs22("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsx25(
      InviteRow,
      {
        recipient,
        onRecipientChange: setRecipient,
        permission,
        onPermissionChange: setPermission,
        expiresInDays,
        onExpiresChange: setExpiresInDays,
        permissions,
        permissionLabels,
        expirations,
        extraFields: inviteExtraFields,
        recipientPlaceholder,
        error,
        disabled: isDelivering,
        onEnter: () => deliver(hasWebShare ? "share" : "email"),
        hidePermission: hideInvitePermission
      }
    ),
    /* @__PURE__ */ jsx25(
      ShareDeliveryButtons,
      {
        onDeliver: deliver,
        canDeliver: recipient.trim().length > 0,
        isDelivering,
        ...hasWebShare === void 0 ? {} : { hasWebShare }
      }
    ),
    success && /* @__PURE__ */ jsx25("p", { className: "text-xs text-[var(--jh-success,#22c55e)]", children: success }),
    /* @__PURE__ */ jsx25(
      CollaboratorList,
      {
        collaborators,
        pendingInvites,
        onRemoveCollaborator,
        canRemoveCollaborator,
        onRevokeInvite,
        onCopyInviteLink: handleCopyInviteLink,
        isLoading,
        renderStatus: renderMemberStatus,
        permissionColors
      }
    ),
    children
  ] });
}

// src/components/TreeNode.tsx
import { useCallback as useCallback9, useMemo as useMemo6, useState as useState12 } from "react";
import { jsx as jsx26, jsxs as jsxs23 } from "react/jsx-runtime";
function TreeNode({
  node,
  depth,
  isSelected = false,
  onSelect,
  onToggle,
  actions = [],
  renderIcon,
  renderLabel,
  children,
  className
}) {
  const [isHovered, setIsHovered] = useState12(false);
  const [contextMenu, setContextMenu] = useState12(null);
  const isFolder = node.type === "folder";
  const folder = isFolder ? node : null;
  const isExpanded = folder?.expanded ?? false;
  const handleClick = useCallback9(() => {
    onSelect?.(node);
  }, [node, onSelect]);
  const handleToggle = useCallback9(
    (e) => {
      e.stopPropagation();
      if (folder && onToggle) {
        onToggle(folder);
      }
    },
    [folder, onToggle]
  );
  const handleContextMenu = useCallback9((e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);
  const closeContextMenu = useCallback9(() => {
    setContextMenu(null);
  }, []);
  const nodeData = useMemo6(() => buildActionNodeData(node, folder), [node, folder]);
  const handleAction = useCallback9(
    (action) => {
      action.onAction(nodeData);
      closeContextMenu();
    },
    [nodeData, closeContextMenu]
  );
  const contextMenuActions = useMemo6(() => {
    return actions.map((action) => ({
      id: action.id,
      label: action.label,
      icon: action.icon,
      shortcut: action.shortcut,
      disabled: action.disabled,
      destructive: action.destructive,
      onAction: () => handleAction(action)
    }));
  }, [actions, handleAction]);
  const defaultIcon = isFolder ? isExpanded ? "\u{1F4C2}" : "\u{1F4C1}" : "\u{1F4C4}";
  const rowActions = actions.slice(0, 2);
  return /* @__PURE__ */ jsxs23("div", { className, children: [
    /* @__PURE__ */ jsxs23(
      "div",
      {
        style: {
          paddingLeft: `${8 + depth * 16}px`
        },
        className: `
          flex items-center py-1 pr-2 cursor-pointer rounded select-none
          transition-colors duration-150
          ${isSelected ? "bg-[var(--jh-primary-light,rgba(59,130,246,0.1))]" : isHovered ? "bg-[var(--jh-bg-hover,#f5f5f5)]" : ""}
        `,
        onClick: handleClick,
        onContextMenu: handleContextMenu,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        children: [
          isFolder && /* @__PURE__ */ jsx26(
            "button",
            {
              type: "button",
              onClick: handleToggle,
              className: "mr-1 p-0.5 border-none bg-transparent cursor-pointer rounded text-xs text-[var(--jh-text-tertiary,#737373)] hover:text-[var(--jh-text-primary,#171717)]",
              "aria-label": isExpanded ? "Collapse folder" : "Expand folder",
              children: isExpanded ? "\u25BC" : "\u25B6"
            }
          ),
          /* @__PURE__ */ jsx26("span", { className: "w-4 h-4 mr-2 flex-shrink-0 flex items-center justify-center", children: renderIcon ? renderIcon(node) : defaultIcon }),
          /* @__PURE__ */ jsx26("span", { className: "flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-[var(--jh-text-primary,#171717)]", children: renderLabel ? renderLabel(node) : node.name }),
          isHovered && rowActions.length > 0 && /* @__PURE__ */ jsx26("div", { className: "flex gap-1 ml-2", children: rowActions.map((action) => /* @__PURE__ */ jsx26(
            "button",
            {
              type: "button",
              onClick: (e) => {
                e.stopPropagation();
                handleAction(action);
              },
              className: `
                  p-1 border-none bg-transparent cursor-pointer rounded text-xs
                  ${action.destructive ? "text-[var(--jh-danger,#ef4444)] hover:bg-red-50" : "text-[var(--jh-text-secondary,#525252)] hover:bg-[var(--jh-bg-hover,#f5f5f5)]"}
                `,
              disabled: action.disabled,
              title: action.label,
              "aria-label": action.label,
              children: action.icon || action.label
            },
            action.id
          )) })
        ]
      }
    ),
    isFolder && isExpanded && children && /* @__PURE__ */ jsx26("div", { role: "group", "aria-label": `Contents of ${node.name}`, children }),
    contextMenu && contextMenuActions.length > 0 && /* @__PURE__ */ jsx26(
      ContextMenu,
      {
        x: contextMenu.x,
        y: contextMenu.y,
        actions: contextMenuActions,
        onClose: closeContextMenu
      }
    )
  ] });
}

// src/components/TreeView.tsx
import {
  getNodeId as getNodeId5
} from "@jbr-jazz/hierarchy-shared";
import { jsx as jsx27 } from "react/jsx-runtime";
function TreeView({
  folders,
  selectedId,
  onSelect,
  onToggle,
  getActions,
  renderIcon,
  renderLabel,
  emptyContent,
  isLoading,
  className
}) {
  const containerClass = `font-sans text-sm ${className || ""}`.trim();
  if (isLoading) {
    return /* @__PURE__ */ jsx27("div", { className: containerClass, children: /* @__PURE__ */ jsx27(LoadingState, { message: "Loading..." }) });
  }
  if (folders.length === 0) {
    return /* @__PURE__ */ jsx27("div", { className: containerClass, children: emptyContent || /* @__PURE__ */ jsx27(EmptyState, {}) });
  }
  return /* @__PURE__ */ jsx27("div", { className: containerClass, role: "tree", children: folders.map((folder) => /* @__PURE__ */ jsx27(
    RecursiveTreeNode2,
    {
      node: folder,
      depth: 0,
      selectedId,
      onSelect,
      onToggle,
      getActions,
      renderIcon,
      renderLabel
    },
    getNodeId5(folder)
  )) });
}
function RecursiveTreeNode2({
  node,
  depth,
  selectedId,
  onSelect,
  onToggle,
  getActions,
  renderIcon,
  renderLabel
}) {
  const nodeId = getNodeId5(node);
  const isSelected = selectedId === nodeId;
  const actions = getActions ? getActions(node) : [];
  const isFolder = node.type === "folder";
  const folder = isFolder ? node : null;
  const childrenList = folder?.children;
  const children = childrenList ? [...childrenList].filter((c) => c !== null && c !== void 0) : [];
  return /* @__PURE__ */ jsx27(
    TreeNode,
    {
      node,
      depth,
      isSelected,
      onSelect,
      onToggle,
      actions,
      renderIcon,
      renderLabel,
      children: children.map((child) => /* @__PURE__ */ jsx27(
        RecursiveTreeNode2,
        {
          node: child,
          depth: depth + 1,
          selectedId,
          onSelect,
          onToggle,
          getActions,
          renderIcon,
          renderLabel
        },
        getNodeId5(child)
      ))
    }
  );
}

export {
  createBetterAuthClient,
  getBetterAuthClient,
  cn,
  buildActionNodeData,
  buttonVariants,
  Button,
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  inputVariants,
  Input,
  Label,
  EmailAuthDialog,
  LinkedEmailsSection,
  ProfileDialog,
  LoadingState,
  CollaboratorList,
  ConfirmDialog,
  CreateDialog,
  MessageDialog,
  SimpleInputDialog,
  ContextMenu,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuRadioGroup,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  EmptyState,
  DraggableTreeNode,
  ReorderDropZone,
  DraggableTreeView,
  MoveDialog,
  InviteRow,
  ShareDeliveryButtons,
  SharePanel,
  TreeNode,
  TreeView
};
